/* oxlint-disable jsx-a11y/no-redundant-roles -- E2E page objects select rows and cells by these role attributes */
import { ComponentType, ReactNode, useLayoutEffect, useRef, VoidFunctionComponent } from 'react';

import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Rotate } from '@components/animators/Rotate';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconOrdering } from '@components/icons/ordering';
import type { SelectionStore } from '@stores/create-selection-store';
import { createSelectionStore } from '@stores/create-selection-store';
import { dropdownMotionConfig } from '@styles/animation';
import { theme } from '@styles/theme';
import { sortByObjectKey } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { transitionFast, transitionSlow } from '@utils/transitions';

import { DataGridActionMenu } from './data-grid-action-menu';
import { handleTableKeydown } from './data-grid-helpers';
import { DataGridSettings as DataGridSettingsMenu } from './data-grid-settings';
import type {
  ActionFn,
  Column,
  Columns,
  ColumnSettings,
  ColumnSettingsChange,
  ColumnSort,
  ColumnType,
  OnRowClick,
} from './data-grid-types';

const expandedSelectionStore = createSelectionStore();

function getColumnWidthRatio(colType?: ColumnType) {
  switch (colType) {
    case 'date':
      return 0.8;
    case 'text-short':
      return 0.7;
    case 'text-long':
      return 1.2;
    case 'numeric':
      return 0.4;
    default:
      return 1;
  }
}

type ColumnSelection<T> = {
  isGroupSelected: boolean | 'mixed';
  onGroupSelect(): void;
  onRowSelect(row: T): void;
  isRowSelected(row: T): boolean;
  overlay?(row: T): ReactNode;
  element?: ReactNode;
};

type ExpansionProps<T> = {
  renderExpandedRow?: VoidFunctionComponent<T>;
};

export type DataGridProps<T> = ExpansionProps<T> & {
  actions?: ActionFn<T>;
  columns: Columns<T>;
  data: Array<T>;
  label: string;
  labelIcon?: ComponentType<IconProps>;
  onRowClick?: OnRowClick<T>;
  isRowInactive?: (data: T) => boolean;

  sort?: ColumnSort<T>;
  onSortChange?: (newSort: ColumnSort<T>) => void;

  columnSettings?: ColumnSettings<T>;
  onColumnSettingsChange?: ColumnSettingsChange<T>;

  selection?: ColumnSelection<T>;
};

function DataGrid<
  T extends {
    id: number | string;
  },
>(props: DataGridProps<T>) {
  const tableRef = useRef<HTMLTableElement>(null as unknown as HTMLTableElement);
  const {
    actions,
    columnSettings,
    columns: userColumns,
    data,
    label,
    onColumnSettingsChange,
    onSortChange,
    sort,
    selection,
    renderExpandedRow,
  } = props;

  const columnsWithSettings = sortByObjectKey(
    userColumns.map((column, idx) => ({
      ...column,
      visible: columnSettings ? columnSettings[column.key]?.visible || false : true,
      order: columnSettings ? columnSettings[column.key]?.order || 0 : idx,
    })),
    'order',
  );
  const columns = columnsWithSettings.filter((column) => column.visible);

  const allowSelect = selection !== undefined;

  const firstColumnIndex = allowSelect ? 0 : 1;
  const lastColumnIndex = columns.length + 1 + (renderExpandedRow ? 1 : 0);
  const lastRowIndex = data.length;

  useLayoutEffect(() => {
    // Whenever the data or columns change, re-focus the first column
    const firstRow = tableRef.current.querySelector('[data-row="1"]');
    const firstCol = firstRow?.querySelector('[data-col="1"]');
    firstCol?.setAttribute('tabindex', '0');
  }, [data.length, columns.length]);

  function onColumnSort(key: keyof T) {
    const column = columns.find((col) => col.key === key);

    if (!column || !column.sortable) return false;

    if (sort?.key === key) {
      onSortChange &&
        onSortChange({
          key,
          order: sort.order === 'ascending' ? 'descending' : 'ascending',
        });
    } else {
      onSortChange && onSortChange({ key, order: 'ascending' });
    }

    return true;
  }

  const columnRatios = columns.map((col) => col.ratio || getColumnWidthRatio(col.type));
  const totalColumnRatio = columnRatios.reduce((sum, ratio) => sum + ratio, 0);
  const hasExtraColumn = Boolean(columnSettings || actions);
  const ids = data.map((item) => item.id);
  const isAllExpanded = expandedSelectionStore.useStore((state) =>
    expandedSelectionStore.isSelected(state, ids),
  );

  return (
    <Table
      ref={tableRef}
      role="grid"
      aria-label={label}
      onKeyDown={handleTableKeydown(
        tableRef,
        firstColumnIndex,
        lastColumnIndex,
        lastRowIndex,
        !!actions,
        (key) => onColumnSort(key as keyof T),
      )}
    >
      <colgroup>
        {allowSelect && <col width="56px" />}
        {columnRatios.map((column, index) => (
          <col key={index} width={(column / totalColumnRatio) * 100 + '%'} />
        ))}
        {hasExtraColumn && <col width="64px" />}
      </colgroup>
      <thead>
        <tr role="row" data-row="0">
          {selection && (
            <th role="columnheader" className="selection" data-col="0" data-key="select">
              <CheckboxContainer $heading>
                <Checkbox
                  tabIndex={-1}
                  value={selection.isGroupSelected}
                  label="Select/Deselect all"
                  labelVisible={false}
                  onChange={selection.onGroupSelect}
                />
              </CheckboxContainer>
            </th>
          )}
          {columns.map((column, index) => (
            <ColumnHeader
              key={column.key as string}
              column={column}
              sort={sort}
              onSortChange={onColumnSort}
              index={index + 1}
            />
          ))}
          {renderExpandedRow && (
            <th role="columnheader" data-col={columns.length + 1} data-actions>
              <Button
                variant="ghost"
                iconLeft={
                  <Rotate isRotated={isAllExpanded}>
                    <IconDirectionRight />
                  </Rotate>
                }
                onClick={() =>
                  isAllExpanded ? expandedSelectionStore.clear() : expandedSelectionStore.add(ids)
                }
              />
            </th>
          )}
          {hasExtraColumn && (
            <th
              role="columnheader"
              data-col={columns.length + 1 + (renderExpandedRow ? 1 : 0)}
              data-actions
            >
              {columnSettings && onColumnSettingsChange && (
                <DataGridSettingsMenu
                  columns={columnsWithSettings}
                  settings={columnSettings}
                  onChange={onColumnSettingsChange}
                />
              )}
            </th>
          )}
        </tr>
      </thead>

      <AnimatePresence>
        {selection?.element && (
          <motion.tbody initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <tr>
              <td colSpan={columns.length + 1 + (allowSelect ? 1 : 0)}>{selection.element}</td>
            </tr>
          </motion.tbody>
        )}
      </AnimatePresence>
      <>
        {data.map((row, index) => (
          <ObservedTableRow
            key={row.id}
            row={row}
            rowIndex={index}
            expandedSelectionStore={expandedSelectionStore}
            hasExtraColumn={hasExtraColumn}
            {...props}
            columns={columns}
          />
        ))}
      </>
    </Table>
  );
}

const _DataGrid = observer(DataGrid);
export { _DataGrid as DataGrid };

type TableRowProps<T> = {
  hasExtraColumn: boolean;
  expandedSelectionStore: SelectionStore;
  rowIndex: number;
  row: T;
} & Pick<
  DataGridProps<T>,
  'actions' | 'columns' | 'isRowInactive' | 'onRowClick' | 'renderExpandedRow' | 'selection'
>;

const TableRow = <
  T extends {
    id: number | string;
  },
>({
  actions,
  columns,
  expandedSelectionStore,
  hasExtraColumn,
  isRowInactive,
  onRowClick,
  renderExpandedRow,
  rowIndex,
  row,
  selection,
}: TableRowProps<T>) => {
  const isChecked = Boolean(selection?.isRowSelected(row));
  const isInactive = Boolean(isRowInactive?.(row));
  const isClickable = Boolean(onRowClick);
  const isExpanded =
    expandedSelectionStore && expandedSelectionStore.useStore((state) => state[row.id]);

  return (
    <tbody>
      <tr
        className={isExpanded ? 'is-expanded' : undefined}
        role="row"
        data-row={rowIndex + 1}
        data-clickable={isClickable}
        data-checked={isChecked}
        data-inactive={isInactive}
        onClick={() => onRowClick?.callback(row)}
        key={`row-${isChecked}`}
      >
        {selection && (
          <td key="select" role="gridcell" data-col={0}>
            {!isChecked && selection.overlay && (
              <SelectionOverlayContainer data-selection-overlay>
                {selection.overlay(row)}
              </SelectionOverlayContainer>
            )}
            <CheckboxContainer data-behind-overlay={Boolean(!isChecked && selection.overlay)}>
              <Checkbox
                tabIndex={-1}
                value={isChecked}
                label="Select/Deselect"
                labelVisible={false}
                onChange={() => selection.onRowSelect(row)}
              />
            </CheckboxContainer>
          </td>
        )}
        {columns.map((column, columnIndex) => (
          <td
            key={column.key as string}
            role="gridcell"
            data-col={columnIndex + 1}
            data-type={column.type}
          >
            <span className={column.center ? 'center' : ''}>
              {column.render ? column.render(row) : row[column.key]}
            </span>
          </td>
        ))}

        {renderExpandedRow && (
          <td role="gridcell" data-col={columns.length + 1} data-actions="true">
            <IconButton
              variant="ghost-purple"
              icon={
                <Rotate isRotated={isExpanded}>
                  <IconDirectionRight />
                </Rotate>
              }
              onClick={(e) => {
                e.preventDefault();
                expandedSelectionStore.toggle(row.id);
              }}
            />
          </td>
        )}
        {hasExtraColumn && (
          <td
            role="gridcell"
            data-col={columns.length + 1 + (renderExpandedRow ? 1 : 0)}
            data-actions="true"
          >
            {actions && <DataGridActionMenu actions={actions} data={row} onRowClick={onRowClick} />}
          </td>
        )}
      </tr>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.tr
            className="expanded-row"
            role="row"
            data-row={rowIndex + 1}
            data-clickable={isClickable}
            data-checked={isChecked}
            data-inactive={isInactive}
            onClick={() => onRowClick?.callback(row)}
          >
            {selection && (
              <motion.td {...expandedRowAnimationConfig} role="gridcell" data-col={0} />
            )}
            <motion.td
              {...expandedRowAnimationConfig}
              data-col={selection ? 1 : 0}
              colSpan={columns.length + 1 + (hasExtraColumn ? 1 : 0)}
            >
              {renderExpandedRow?.(row)}
            </motion.td>
          </motion.tr>
        )}
      </AnimatePresence>
    </tbody>
  );
};

const ObservedTableRow = observer(TableRow);

const expandedRowAnimationConfig: AnimationProps = {
  initial: { height: 0, opacity: 0, y: -25, paddingBottom: 0 },
  animate: {
    paddingBottom: pxToRem(8),
    height: 'auto',
    opacity: 1,
    y: 0,
    transition: dropdownMotionConfig.transition,
  },
};

function ColumnHeader<T>({
  column,
  index,
  onSortChange,
  sort,
}: {
  column: Column<T>;
  index: number;
  onSortChange?: (key: keyof T) => void;
  sort?: ColumnSort<T>;
}) {
  const sortingBy = sort?.key === column.key;

  return (
    <th
      role="columnheader"
      data-col={index}
      data-key={column.key}
      aria-sort={column.sortable ? (sortingBy ? sort.order : 'none') : undefined}
      onClick={column.sortable && onSortChange ? () => onSortChange(column.key) : undefined}
    >
      <div className={`columnheader ${column.center ? 'center' : ''}`}>
        <div className="label-container">
          {column.labelIcon ? (
            <column.labelIcon color="purple50" tooltip={column.label} />
          ) : (
            column.label
          )}
        </div>
        <span className={sortingBy ? 'sorting-by ' + sort.order : 'hidden'}>
          <IconOrdering color="elementPurple50" />
        </span>
      </div>
    </th>
  );
}

const SelectionOverlayContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  zIndex: 2,
});

const CheckboxContainer = styled.span<{ $heading?: boolean }>((p) => ({
  height: '2.5rem',
  width: '2.5rem',
  padding: '0 !important',
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  ...(p.$heading && {
    padding: '0 0.5rem',
  }),
  ':hover': {
    cursor: 'pointer',
  },
}));

const Table = styled.table({
  tableLayout: 'fixed',
  background: theme.color.backgroundLightDark,
  width: '100%',
  borderSpacing: 0,
  color: theme.color.textPrimary,
  minWidth: 1000,
  overflowX: 'auto',

  thead: {
    font: theme.font.semibold14,
    tr: {
      th: {
        borderBottom: `0.0625rem solid ${theme.color.border10}`,
        background: theme.color.backgroundLightDark,
        color: theme.color.textSecondary,
        zIndex: 10,
        position: 'sticky',
        top: 0,
        textAlign: 'left',
        height: '3.5rem',

        '&[data-actions="true"]': {
          width: '4.5rem',
        },

        '&[aria-sort]:hover, &.selection:hover': {
          cursor: 'pointer',
        },

        '&[aria-sort="none"]:hover': {
          color: theme.color.textSecondary,
        },

        '&[aria-sort="ascending"], &[aria-sort="descending"]': {
          color: theme.color.textSecondary,
        },

        '&:first-child': {
          paddingLeft: '1rem',
        },

        '&:last-child': {
          paddingRight: '1.5rem',
        },

        '.center': {
          justifyContent: 'center',
        },

        '.columnheader': {
          display: 'flex',
          alignItems: 'center',
          padding: '0 0.5rem',
          border: '0.0625rem solid transparent',
          height: '100%',

          '.label-container': {
            position: 'relative',
            height: '1.5rem',
            lineHeight: '1.5rem',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },

          '.hidden': {
            visibility: 'hidden',
            position: 'absolute',
          },

          '.sorting-by': {
            display: 'block',
            transition: transitionSlow('transform'),
            height: '1.5rem',
            width: '1.5rem',
            position: 'relative',

            '&.ascending': {
              transform: 'rotate(-180deg)',
            },
          },
        },
      },
    },
  },

  tbody: {
    font: theme.font.regular14,

    '&:hover tr[data-checked="false"]': {
      backgroundColor: theme.color.uiTrans5,
      transition: transitionFast('all'),
    },

    tr: {
      ':not(.is-expanded)>td': {
        borderBottom: `0.0625rem solid ${theme.color.border10}`,
      },
      '&.expanded-row>td': {
        verticalAlign: 'middle',
      },
      td: {
        position: 'relative',
        height: '3.5rem',

        a: {
          display: 'block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },

        '> span': {
          padding: '0 0.5rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
        },
        '.center': {
          display: 'flex',
          justifyContent: 'center',
        },

        '&:first-child': {
          paddingLeft: '1rem',
        },

        '&:last-child': {
          paddingRight: '1.5rem',
        },
      },

      '&[data-checked="true"]': {
        backgroundColor: theme.color.greenTrans5,
        transition: transitionFast('all'),
      },

      '&[data-inactive="true"]': {
        backgroundColor: theme.color.uiTrans5,
        transition: transitionFast('all'),
      },

      '&[data-clickable="true"]:hover': {
        cursor: 'pointer',
      },

      // `visibility`, so hiding it does not collapse the column's width.
      '[data-behind-overlay="true"]': {
        visibility: 'hidden',
      },

      ':hover, :focus-within': {
        '[data-selection-overlay="true"]': {
          display: 'none',
        },
        '[data-behind-overlay="true"]': {
          visibility: 'visible',
        },
      },
    },
  },

  'th:focus-visible': {
    outline: 'none',
    '.columnheader': {
      font: theme.font.semibold14,
      color: theme.color.elementBlue100,
    },
  },

  'td:focus-visible': {
    outline: 'none',
    '> span': {
      font: theme.font.semibold14,
      color: theme.color.elementBlue100,
    },
  },
});
